import { FC, useState, useCallback } from 'react';
import { ActionList, Button, Divider, InlineStack, Popover, Text } from '@shopify/polaris';
import {
  CollectionFilledIcon,
  DomainLandingPageIcon,
  EmailIcon,
  LinkIcon,
  LogoXIcon,
  LogoInstagramIcon,
  EditIcon,
  MenuVerticalIcon,
  DeleteIcon,
  ComposeIcon,
  ImportIcon,
  OutgoingIcon,
} from '@shopify/polaris-icons';
import { StudioVideoStatus, type StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';
import { DownloadStudioVideoModal } from '@/features/publish/DownloadStudioVideoModal';
import useGetPlayerUrl from '@/hooks/useGetPlayerUrl';
import { SocialPlatform } from '@/constants/socialPlatforms';
import { EVENTS } from '@/constants/tracking';
import StudioRoutes from '@/utils/routes';
import { getSocialPlatformLink } from '@/utils/socialPlatforms';
import { track } from '@/utils/tracker';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import { RenameStudioVideoModal } from './RenameStudioVideoModal';
import { DeleteStudioVideoModal } from './DeleteStudioVideoModal';
import PublishToInstagramModal from './PublishToInstagramModal';
import { FEATURE_INSTAGRAM_INTEGRATION } from '@tolstoy/constants/features.constants';
import { useAccountFeatures } from '@/components/studio/hooks/useAccountFeatures';
import { StudioVideoVersion } from '../video-generation-stepper/types';
import { useShallow } from 'zustand/react/shallow';
import { useEditorStore } from '../editor/store/editorStore';
import { TemplateName } from '../studio-video-templates/types/template';

interface Props {
  video: StudioVideo;
  disabled: boolean;
  isDraft?: boolean;
}

export const ShareVideoButton: FC<Props> = ({ video, disabled, isDraft }) => {
  const [popoverActive, setPopoverActive] = useState(false);
  const [editPopoverActive, setEditPopoverActive] = useState(false);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isInstagramModalOpen, setIsInstagramModalOpen] = useState(false);
  const { setEditorData } = useEditorStore(
    useShallow((state) => ({
      setEditorData: state.setEditorData,
    }))
  );

  const playerUrl = useGetPlayerUrl(video);
  const { pushPath } = useRouterNavigation();
  const shouldOpenEditor = !!(
    'project' in video &&
    video.project &&
    (video.template === TemplateName.Highlight || video.status === StudioVideoStatus.DRAFT)
  );

  const togglePopoverActive = useCallback(() => setPopoverActive((popoverActive) => !popoverActive), []);
  const toggleEditPopoverActive = useCallback(
    () => setEditPopoverActive((editPopoverActive) => !editPopoverActive),
    []
  );
  const handleSocialPlatformClick = async (platform: SocialPlatform) => {
    track(EVENTS.SHARE_ON_SOCIAL_CLICK, { platform });
    const shareUrl = getSocialPlatformLink(platform, playerUrl);
    window.open(shareUrl, '_blank');
    togglePopoverActive();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(playerUrl);
    togglePopoverActive();
  };

  const handlePublishToWebsite = () => {
    track(EVENTS.PUBLISH_TO_WEBSITE_CLICK);
    pushPath(StudioRoutes.publish());
    togglePopoverActive();
  };

  const handleFeatureOnShop = () => {
    track(EVENTS.FEATURE_ON_SHOP_CLICK);
    pushPath(StudioRoutes.publish());
    togglePopoverActive();
  };

  const handlePublishToEmailFlows = () => {
    track(EVENTS.PUBLISH_TO_EMAIL_FLOW_CLICK);
    pushPath(StudioRoutes.publish());
    togglePopoverActive();
  };

  const handleOpenEditor = useCallback(() => {
    if (!shouldOpenEditor || !video.project?.generation) return;

    let generation = video.project.generation;
    let studioVideo = video;

    if (video.status !== StudioVideoStatus.DRAFT) {
      const { id: studioVideoId, createdAt, updatedAt, ...newStudioVideo } = video;
      studioVideo = newStudioVideo;

      const { id: generationId, ...newGeneration } = video.project.generation;
      generation = newGeneration;
    }

    setEditorData({ generation, studioVideo, project: video.project });

    pushPath(StudioRoutes.editor());
  }, [pushPath, video, shouldOpenEditor, setEditorData]);

  const handleEditClick = useCallback(() => {
    if (shouldOpenEditor) {
      handleOpenEditor();
    } else {
      pushPath(StudioRoutes.createProject({ id: video.id }));
    }
  }, [handleOpenEditor, shouldOpenEditor, pushPath, video]);

  const { data } = useAccountFeatures();
  const instagramFeature = data?.[FEATURE_INSTAGRAM_INTEGRATION];
  const isInstagramPublishEnabled = instagramFeature?.enabled && instagramFeature?.settings?.publish;
  // hiding duplicate and edit for influencer videos as it doesn't work right now
  const isInfluencerVideo = video.template === TemplateName.InfluencerAvatar;

  return (
    <>
      <DownloadStudioVideoModal isOpen={isDownloadDialogOpen} setIsOpen={setIsDownloadDialogOpen} video={video} />
      <InlineStack gap="200" align="space-between">
        <InlineStack gap="200">
          {isDraft ? (
            <Button onClick={handleEditClick} icon={ComposeIcon} size="large">
              Edit
            </Button>
          ) : (
            <>
              <Popover
                active={popoverActive}
                activator={
                  <Button onClick={togglePopoverActive} icon={OutgoingIcon} size="large" disabled={disabled} />
                }
                onClose={togglePopoverActive}
              >
                <Popover.Pane>
                  <Popover.Section>
                    <Text as="h3" variant="headingMd">
                      Publish
                    </Text>
                  </Popover.Section>
                  <ActionList
                    actionRole="menuitem"
                    items={[
                      {
                        content: 'Publish to Website',
                        icon: DomainLandingPageIcon,
                        onAction: handlePublishToWebsite,
                      },
                      {
                        content: 'Feature on Shop',
                        icon: CollectionFilledIcon,
                        onAction: handleFeatureOnShop,
                      },
                      {
                        content: 'Publish to Email Flows',
                        icon: EmailIcon,
                        onAction: handlePublishToEmailFlows,
                        disabled: !isInstagramPublishEnabled,
                      },
                    ]}
                  />
                </Popover.Pane>

                <Divider />

                <Popover.Pane>
                  <Popover.Section>
                    <Text as="h3" variant="headingMd">
                      Share
                    </Text>
                  </Popover.Section>
                  <ActionList
                    actionRole="menuitem"
                    items={[
                      {
                        content: 'Copy Link',
                        icon: LinkIcon,
                        onAction: handleCopyLink,
                      },
                      {
                        content: 'Share on X',
                        icon: LogoXIcon,
                        onAction: () => handleSocialPlatformClick(SocialPlatform.X),
                      },
                      ...(isInstagramPublishEnabled
                        ? [
                            {
                              content: 'Share on Instagram',
                              icon: LogoInstagramIcon,
                              onAction: () => setIsInstagramModalOpen(true),
                            },
                          ]
                        : []),
                    ]}
                  />
                </Popover.Pane>
              </Popover>

              <Button
                onClick={() => setIsDownloadDialogOpen(true)}
                icon={ImportIcon}
                size="large"
                disabled={disabled || isDraft}
              />
            </>
          )}
        </InlineStack>

        <Popover
          active={editPopoverActive}
          activator={
            <Button
              onClick={toggleEditPopoverActive}
              icon={MenuVerticalIcon}
              size="large"
              disabled={disabled && !isDraft}
            />
          }
          onClose={toggleEditPopoverActive}
        >
          <Popover.Pane>
            <ActionList
              actionRole="menuitem"
              items={[
                {
                  content: shouldOpenEditor ? 'Edit' : 'Duplicate and Edit',
                  icon: ComposeIcon,
                  disabled: isDraft || video.version !== StudioVideoVersion.v2 || isInfluencerVideo,
                  onAction: handleEditClick,
                },
                {
                  content: 'Rename Creation',
                  icon: EditIcon,
                  onAction: () => setIsEditDialogOpen(true),
                },
                {
                  content: 'Delete Creation',
                  icon: DeleteIcon,
                  onAction: () => setIsDeleteDialogOpen(true),
                },
              ]}
            />
          </Popover.Pane>
        </Popover>
      </InlineStack>

      <RenameStudioVideoModal video={video} isOpen={isEditDialogOpen} setIsOpen={setIsEditDialogOpen} />
      <DeleteStudioVideoModal video={video} isOpen={isDeleteDialogOpen} setIsOpen={setIsDeleteDialogOpen} />
      <PublishToInstagramModal video={video} isOpen={isInstagramModalOpen} setIsOpen={setIsInstagramModalOpen} />
    </>
  );
};
